// TODO: replace with date-fns
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { Badge, Button, Form } from "react-bootstrap";
import { Link, useHistory } from "react-router-dom";
import {
  type Case,
  type Client,
  CommunicationStatus,
  DocumentType,
} from "@/briefpoint-client";
import { FormModal } from "@/components/Modals";
import { useAuth } from "@/hooks/useAuth";
import useDocuments from "@/hooks/useDocuments";
import WordIcon from "@/images/new-doc-icon.svg?react";
import PdfIcon from "@/images/new-pdf-icon.svg?react";
import { CaliforniaId } from "@/lib/constants";
import type { DocumentClient, ViewCommunicationResponse } from "@/lib/types";
import { dispatchSort, sortCommunications } from "@/lib/utils";
import styles from "./DocumentList.module.scss";
import { DocumentRowWithReview } from "./DocumentRowWithReview";
import SelectCaseModal from "./SelectCaseModal";

export function DocumentIcon({ extension }: { extension: string | undefined }) {
  if (!!extension && ["docx", "doc"].includes(extension)) {
    return <WordIcon className="fs-2 text-primary" />;
  }
  return <PdfIcon className="fs-2 text-danger" />;
}

export function StartButton({ documentId }: { documentId: string }) {
  return (
    <Link
      className="btn btn-outline-primary btn-26"
      to={`/wizard/${documentId}/source-document-review?document_type=false`}
    >
      Start
    </Link>
  );
}

export function ReviewButton({
  documentId,
  type,
}: {
  documentId: string;
  type: DocumentType;
}) {
  return (
    <Link
      className="btn btn-outline-primary btn-26"
      to={`/wizard/${type === DocumentType.Pleading ? "propound/" : ""}${documentId}`}
    >
      Review
    </Link>
  );
}

export function FinishButton({
  documentId,
  type,
}: {
  documentId: string;
  type: DocumentType;
}) {
  return (
    <Link
      className="btn btn-outline-primary btn-26"
      to={`/wizard/${type === DocumentType.Pleading ? "propound/" : ""}${documentId}`}
    >
      Continue
    </Link>
  );
}

export function FinishDraftButton({ document }: { document: DocumentClient }) {
  return (
    <Link
      className="btn btn-outline-primary btn-26"
      to={`/clients/${document.clientId}/cases/${document.caseId}/generateDiscoveryRequest?documentId=${document.id}`}
    >
      {document.lastWordDownloadDate ? "Edit" : "Finish"}
    </Link>
  );
}

export function DownloadButton({ doc }: { doc: DocumentClient }) {
  const [, , , , , , , , downloadRequest] = useDocuments(false);

  return (
    <Button
      className="btn btn-outline-primary btn-26"
      onClick={() => downloadRequest(doc)}
    >
      Download
    </Button>
  );
}

export function DueSoon(dueDate?: string) {
  if (!dueDate) return "​";

  const diff = DateTime.fromISO(dueDate).diff(DateTime.now(), "days");

  if (diff.days < -1 || diff.days > 7) return "​";

  return diff.days < 0 ? (
    <Badge bg="danger">Due Today</Badge>
  ) : (
    <Badge bg="warning">Due Soon</Badge>
  );
}

export function NewComm(
  comm?: ViewCommunicationResponse,
  document?: DocumentClient,
) {
  if (!comm?.lastActivityDate || comm.status !== CommunicationStatus.Complete)
    return "​";

  if (
    !document?.info?.lastViewDate ||
    comm.lastActivityDate < document.info.lastViewDate
  ) {
    return <Badge bg="success">New</Badge>;
  }

  return "​";
}

// convert from yyyy-mm-dd to mm/dd/yyyy
export function formatLocalDate(date?: string) {
  if (!date) return;

  const parts = date.split("-");

  return `${parts[1]}/${parts[2]}/${parts[0]}`;
}

export function sortDocumentDates(
  first: DocumentClient,
  second: DocumentClient,
  sortColumn: string,
  sortDirectionAscending: boolean,
) {
  // I couldn't get realDueDate as a computed property to work, sorry
  const a = (
    (sortColumn === "realDueDate"
      ? toDateOrMin(first.info?.dueDateOverridden || first.info?.dueDate)
      : (first[sortColumn as keyof typeof first] as Date)) ??
    new Date(-8640000000000000)
  ).getTime();
  const b = (
    (sortColumn === "realDueDate"
      ? toDateOrMin(second.info?.dueDateOverridden || second.info?.dueDate)
      : (second[sortColumn as keyof typeof second] as Date)) ??
    new Date(-8640000000000000)
  ).getTime();

  return sortDirectionAscending ? a - b : b - a;
}

export function toDateOrMin(val?: string | null) {
  return val ? new Date(val) : new Date(-8640000000000000);
}

export default function DocumentList({
  documents,
  archiveDocument,
  updateDocument,
  allCases,
  allClients,
  downloadForm,
  downloadRequest,
  externalCommunications,
  removeClientAccess,
  updateCommunication,
}: {
  documents: DocumentClient[];
  archiveDocument: (document: DocumentClient) => Promise<void>;
  updateDocument: (
    document: DocumentClient,
    _case?: Case,
    client?: Client,
  ) => Promise<void>;
  allCases?: Case[];
  allClients?: Client[];
  downloadForm: (
    document: DocumentClient,
    formId: string,
    formTypeName: string,
  ) => Promise<void>;
  downloadRequest: (document: DocumentClient) => Promise<void>;
  externalCommunications?: ViewCommunicationResponse[];
  removeClientAccess: (
    communication: ViewCommunicationResponse,
  ) => Promise<void>;
  updateCommunication: (
    communication: ViewCommunicationResponse,
  ) => Promise<void>;
}) {
  const { firm, jurisdictions } = useAuth();
  const [showEditDueDate, setShowEditDueDate] = useState<
    DocumentClient | undefined
  >();
  const [showSetCase, setShowSetCase] = useState<DocumentClient | undefined>();
  const [dueDateOverride, setDueDateOverride] = useState<string | undefined>();
  const [sortColumn, setSortColumns] = useState("uploadDate");
  const [sortDirectionAscending, setSortDirectionAscending] = useState(false);
  const history = useHistory();

  function getCommunicationForDoc(doc: DocumentClient) {
    return externalCommunications
      ?.filter((c) => c.objectId === doc.id)
      ?.sort(sortCommunications)?.[0];
  }

  //TODO: when multiple jx per firm need to revisit to determine how to know to show DueDate stuff
  const jurisdiction = useMemo(() => {
    if (!jurisdictions || !firm) {
      return undefined;
    }

    const firmJx =
      firm.offices?.find((x) => x.jurisdictions?.length)?.jurisdictions?.[0]
        ?.jurisdictionId ?? CaliforniaId;
    const jx = jurisdictions?.find((j) => j.id === firmJx);
    return jx;
  }, [jurisdictions, firm]);

  function closeDueDateModal() {
    setShowEditDueDate(undefined);
    setDueDateOverride(undefined);
  }

  async function saveDocumentDueDate(
    showEditDueDate: DocumentClient,
  ): Promise<void> {
    if (showEditDueDate.info) {
      showEditDueDate.info.dueDateOverridden = dueDateOverride;
    }
    await updateDocument(showEditDueDate);
    closeDueDateModal();
  }

  function toDateOrNow(val?: string | null) {
    return val ? DateTime.fromISO(val) : DateTime.now();
  }

  function changeSort(column: string) {
    if (column === sortColumn) {
      setSortDirectionAscending(!sortDirectionAscending);
    } else {
      setSortColumns(column);
      setSortDirectionAscending(false);
    }
  }

  function addDueDateDays(days?: number) {
    const existing = toDateOrNow(
      showEditDueDate?.info?.dueDateOverridden ||
        showEditDueDate?.info?.dueDate,
    );

    const newDate = days
      ? existing.plus({ days })
      : existing.plus({ month: 1 });

    setDueDateOverride(newDate.toFormat("yyyy-MM-dd"));
  }

  function createReview(doc: DocumentClient) {
    history.push(`/documents/${doc.id}/client-feedback`);
  }

  const updateDocumentName = async (
    newName: string,
    document: DocumentClient,
  ): Promise<void> => {
    const updatedDocument = { ...document, name: newName };
    await updateDocument(updatedDocument);
  };

  return (
    <>
      <div className={styles.documentListTableWrapper}>
        <table id="document-list-table" className={styles.documentListTable}>
          <thead>
            <tr>
              <th className={styles.columnIcon} />
              {/* TODO: replace with button */}
              <th
                className={`${styles.columnDocumentName} clickable ${sortColumn === "name" ? "sorting" : ""} ${sortDirectionAscending ? "ascending" : "descending"}`}
                style={{ minWidth: "125px" }}
                onClick={() => changeSort("name")}
              >
                Document Name
              </th>
              {/* TODO: replace with button */}
              <th
                className={`${styles.columnDocType} clickable ${sortColumn === "type" ? "sorting" : ""} ${sortDirectionAscending ? "ascending" : "descending"}`}
                style={{ width: "93px", minWidth: "93px" }}
                onClick={() => changeSort("type")}
              >
                Doc Type
              </th>
              {/* TODO: replace with button */}
              <th
                className={`${styles.columnCase} clickable ${sortColumn === "case" ? "sorting" : ""} ${sortDirectionAscending ? "ascending" : "descending"}`}
                onClick={() => changeSort("case")}
                style={{ minWidth: "140px", width: "140px" }}
              >
                Case
              </th>
              {/* TODO: replace with button */}
              <th
                className={`${styles.columnUploadDate} clickable ${sortColumn === "uploadDate" ? "sorting" : ""} ${sortDirectionAscending ? "ascending" : "descending"}`}
                style={{ width: "93px" }}
                onClick={() => changeSort("uploadDate")}
              >
                Upload Date
              </th>
              {!!jurisdiction?.supportsDueDates && (
                <>
                  {/* TODO: replace with button */}
                  <th
                    className={`${styles.columnDueDate} clickable ${sortColumn === "realDueDate" ? "sorting" : ""} ${sortDirectionAscending ? "ascending" : "descending"}`}
                    onClick={() => changeSort("realDueDate")}
                    style={{ width: "93px" }}
                  >
                    Due Date
                  </th>
                  <th className="column-due-soon" />
                </>
              )}
              {/* // TODO: replace with button */}
              <th
                className={`${styles.columnClient} clickable ${sortColumn === "client" ? "sorting" : ""} ${sortDirectionAscending ? "ascending" : "descending"}`}
                style={{ width: "120px", minWidth: "120px" }}
                onClick={() => changeSort("client")}
              >
                Client
              </th>

              <th className={styles.columnClientActivity}>Client Activity</th>
              <th className={styles.columnAction} style={{ width: "120px" }} />
              <th className={styles.columnKebab} />
            </tr>
          </thead>
          <tbody>
            {documents
              .sort((a, b) =>
                dispatchSort(
                  a,
                  b,
                  sortColumn,
                  sortDirectionAscending,
                  allCases,
                  allClients,
                ),
              )
              .map((doc) => (
                <DocumentRowWithReview
                  key={doc.id}
                  doc={doc}
                  archiveDocument={archiveDocument}
                  editDueDate={
                    jurisdiction?.supportsDueDates
                      ? setShowEditDueDate
                      : undefined
                  }
                  _case={
                    doc.caseId
                      ? allCases?.find((x) => x.id === doc.caseId)
                      : undefined
                  }
                  setShowSetCase={doc.caseId ? undefined : setShowSetCase}
                  downloadForm={downloadForm}
                  downloadRequest={downloadRequest}
                  createReview={createReview}
                  communication={getCommunicationForDoc(doc)}
                  removeClientAccess={removeClientAccess}
                  updateCommunication={updateCommunication}
                  updateDocumentName={(name) => updateDocumentName(name, doc)}
                  client={allClients?.find((c) => c.id === doc.clientId)}
                />
              ))}
          </tbody>
        </table>
      </div>
      <SelectCaseModal
        doc={showSetCase}
        onClose={() => setShowSetCase(undefined)}
        saveCaseClient={updateDocument}
        allCases={allCases}
        allClients={allClients}
      />
      <FormModal
        show={!!showEditDueDate}
        title={`${showEditDueDate?.info?.dueDateOverridden || showEditDueDate?.info?.dueDate ? "Edit" : "Set"} Due Date`}
        size="sm"
        confirmText="Confirm"
        cancelText="Cancel"
        onClose={closeDueDateModal}
        onConfirm={() => {
          if (showEditDueDate) {
            saveDocumentDueDate(showEditDueDate);
          }
        }}
      >
        {(showEditDueDate?.info?.dueDateOverridden ||
          showEditDueDate?.info?.dueDate) && (
          <div className={styles.addXDays}>
            Add{" "}
            <Button variant="link" onClick={() => addDueDateDays(7)}>
              7 days
            </Button>
            ,{" "}
            <Button variant="link" onClick={() => addDueDateDays(14)}>
              14 days
            </Button>
            ,{" "}
            <Button onClick={() => addDueDateDays()} variant="link">
              1 month
            </Button>
          </div>
        )}
        <Form.Control
          type="date"
          className={styles.datePicker}
          value={
            dueDateOverride ||
            showEditDueDate?.info?.dueDateOverridden ||
            showEditDueDate?.info?.dueDate ||
            undefined
          }
          onChange={(e) => setDueDateOverride(e.target.value)}
        />
      </FormModal>
    </>
  );
}
